/**
 * Tax treatment of categories + aggregation of tagged bank transactions into
 * a partnership return and GST/BAS figures.
 *
 * The Cash Flow page already tags every line with a `category` (income:* /
 * expense:* / internal_transfer / uncategorised) and a `scope` (business vs
 * family). This module reads those tags — it is the ONLY classifier the tax
 * view uses; it never re-parses descriptions.
 *
 * Key partnership rules baked in as defaults (all user-overridable in the UI):
 *   - Partner drawings (expense:wages, "TO Benjamin Ille" etc.) are NOT
 *     deductible — they're a distribution of profit, not an expense.
 *   - ATO payments (expense:tax — income tax / GST remitted / PAYG) are not
 *     deductions and carry no GST credit.
 *   - Interest and bank fees are input-taxed → no GST.
 *   - Home / renovation is private → not deductible.
 */

import { BankTransaction } from '../types';
import { getCategoryLabel } from './categories';
import {
  FinancialYear,
  financialYearRange,
  exGst,
  gstFromInclusive,
  roundMoney,
  estimateIndividualTax,
  IndividualTaxResult,
} from './auTax';

export interface CategoryTaxTreatment {
  /** Allowable business deduction (expense side). */
  deductible: boolean;
  /** Standard-rated for GST — income → 1A collected, expense → 1B credit. */
  gstApplicable: boolean;
  /** Assessable income (income side). */
  assessable: boolean;
}

/** Per-category deviations from the bucket defaults. */
const CATEGORY_OVERRIDES: Record<string, Partial<CategoryTaxTreatment>> = {
  'income:interest': { gstApplicable: false }, // input-taxed financial supply
  'income:non_assessable': { assessable: false, gstApplicable: false }, // FTB / Medicare — tax-free
  'expense:wages': { deductible: false, gstApplicable: false }, // drawings; wages carry no GST
  'expense:tax': { deductible: false, gstApplicable: false }, // ATO remittances
  'expense:interest': { gstApplicable: false },
  'expense:bank_fees': { gstApplicable: false },
  'expense:home': { deductible: false }, // private / renovation
  'expense:groceries': { gstApplicable: false }, // basic food is GST-free
};

/** Bucket + override defaults for a category, before user overrides. */
export function defaultTreatment(categoryId: string): CategoryTaxTreatment {
  if (categoryId === 'internal_transfer' || categoryId === 'uncategorised') {
    return { deductible: false, gstApplicable: false, assessable: false };
  }
  const bucket = categoryId.split(':')[0];
  let base: CategoryTaxTreatment;
  if (bucket === 'income') base = { deductible: false, gstApplicable: true, assessable: true };
  else if (bucket === 'expense') base = { deductible: true, gstApplicable: true, assessable: false };
  else base = { deductible: false, gstApplicable: false, assessable: false };
  return { ...base, ...(CATEGORY_OVERRIDES[categoryId] || {}) };
}

export type CategoryTaxOverrides = Record<string, Partial<CategoryTaxTreatment>>;

/** Final treatment = bucket default ← seed override ← user override. */
export function resolveCategoryTax(categoryId: string, userOverrides?: CategoryTaxOverrides): CategoryTaxTreatment {
  return { ...defaultTreatment(categoryId), ...(userOverrides?.[categoryId] || {}) };
}

// ---------------------------------------------------------------------------
// Aggregation
// ---------------------------------------------------------------------------

export interface CategoryLine {
  category: string;
  label: string;
  /** GST-inclusive magnitude (always ≥ 0). */
  gross: number;
  /** GST-exclusive magnitude (= gross when not GST-applicable / not registered). */
  exGst: number;
  /** GST component (0 when not applicable / not registered). */
  gst: number;
  count: number;
  gstApplicable: boolean;
}

export interface PartnershipReturn {
  fy: FinancialYear;
  income: CategoryLine[];
  deductions: CategoryLine[];
  excluded: {
    drawings: CategoryLine[];
    atoPayments: CategoryLine[];
    nonDeductible: CategoryLine[];
    internalTransfers: { count: number; gross: number };
  };
  totals: {
    assessableIncomeExGst: number;
    deductionsExGst: number;
    netIncome: number;
    gstCollected: number; // BAS 1A
    gstPaid: number; // BAS 1B
    gstNet: number; // 1A − 1B (positive = payable)
  };
  warnings: {
    uncategorisedBusinessCount: number;
    uncategorisedBusinessGross: number;
    unassignedCount: number;
    unassignedGross: number;
  };
  /** Business transactions counted (excludes internal transfers). */
  businessTxCount: number;
}

export interface PartnershipReturnOptions {
  fy: FinancialYear;
  gstRegistered: boolean;
  categoryOverrides?: CategoryTaxOverrides;
  /** Treat expense:wages as deductible employee wages rather than drawings. */
  wagesAreEmployees?: boolean;
}

interface Bucketed {
  category: string;
  gross: number; // magnitude
  count: number;
}

function inFinancialYear(dateISO: string, fy: FinancialYear): boolean {
  const { start, end } = financialYearRange(fy);
  return dateISO >= start && dateISO <= end;
}

function toLine(b: Bucketed, gstApplicable: boolean, registered: boolean): CategoryLine {
  const applies = registered && gstApplicable;
  return {
    category: b.category,
    label: getCategoryLabel(b.category),
    gross: roundMoney(b.gross),
    exGst: exGst(b.gross, applies),
    gst: applies ? gstFromInclusive(b.gross) : 0,
    count: b.count,
    gstApplicable,
  };
}

const sumEx = (lines: CategoryLine[]) => roundMoney(lines.reduce((s, l) => s + l.exGst, 0));
const sumGst = (lines: CategoryLine[]) => roundMoney(lines.reduce((s, l) => s + l.gst, 0));

/**
 * Build the partnership return from the FULL transaction list. Filters to the
 * financial year (cash basis, by date) and to the business books; groups by
 * category with signed netting (so a refund reduces its category), then routes
 * each category into income / deductions / excluded per its tax treatment.
 */
export function buildPartnershipReturn(
  transactions: BankTransaction[],
  opts: PartnershipReturnOptions,
): PartnershipReturn {
  const { fy, gstRegistered } = opts;

  // Group business-scoped rows by category using SIGNED sums (refunds net).
  const groups = new Map<string, { signed: number; count: number }>();
  let internalCount = 0;
  let internalGross = 0;
  let uncatCount = 0;
  let uncatGross = 0;
  let unassignedCount = 0;
  let unassignedGross = 0;
  let businessTxCount = 0;

  for (const t of transactions) {
    if (!inFinancialYear(t.date, fy)) continue;

    // Unassigned-books warning spans all accounts (these would be missed by a
    // business-only scan yet may belong in the partnership books).
    if (!t.scope) {
      unassignedCount++;
      unassignedGross += Math.abs(t.amount);
    }

    if (t.scope !== 'business') continue;

    if (t.category === 'internal_transfer') {
      internalCount++;
      internalGross += Math.abs(t.amount);
      continue;
    }
    if (t.category === 'uncategorised') {
      uncatCount++;
      uncatGross += Math.abs(t.amount);
      // still count as a business row so the user sees it's unclassified
      businessTxCount++;
      continue;
    }

    businessTxCount++;
    const g = groups.get(t.category) || { signed: 0, count: 0 };
    g.signed += t.amount;
    g.count += 1;
    groups.set(t.category, g);
  }

  const income: CategoryLine[] = [];
  const deductions: CategoryLine[] = [];
  const drawings: CategoryLine[] = [];
  const atoPayments: CategoryLine[] = [];
  const nonDeductible: CategoryLine[] = [];

  for (const [category, g] of Array.from(groups.entries())) {
    const treat = resolveCategoryTax(category, opts.categoryOverrides);
    const bucket = category.split(':')[0];
    const b: Bucketed = { category, gross: Math.abs(g.signed), count: g.count };
    const line = toLine(b, treat.gstApplicable, gstRegistered);

    if (bucket === 'income') {
      if (treat.assessable) income.push(line);
      else nonDeductible.push(line); // non-assessable income parked as excluded
      continue;
    }
    // expense bucket (or anything else the user invented)
    const isDrawings = category === 'expense:wages' && !opts.wagesAreEmployees;
    const isEmployeeWages = category === 'expense:wages' && !!opts.wagesAreEmployees;
    if (isDrawings) {
      drawings.push(line);
    } else if (category === 'expense:tax') {
      atoPayments.push(line);
    } else if (isEmployeeWages) {
      deductions.push(line); // genuine employee wages are deductible (still no GST)
    } else if (!treat.deductible) {
      nonDeductible.push(line);
    } else {
      deductions.push(line);
    }
  }

  const byGrossDesc = (a: CategoryLine, b: CategoryLine) => b.gross - a.gross;
  income.sort(byGrossDesc);
  deductions.sort(byGrossDesc);
  drawings.sort(byGrossDesc);
  atoPayments.sort(byGrossDesc);
  nonDeductible.sort(byGrossDesc);

  const assessableIncomeExGst = sumEx(income);
  const deductionsExGst = sumEx(deductions);
  const gstCollected = sumGst(income);
  const gstPaid = sumGst(deductions);

  return {
    fy,
    income,
    deductions,
    excluded: {
      drawings,
      atoPayments,
      nonDeductible,
      internalTransfers: { count: internalCount, gross: roundMoney(internalGross) },
    },
    totals: {
      assessableIncomeExGst,
      deductionsExGst,
      netIncome: roundMoney(assessableIncomeExGst - deductionsExGst),
      gstCollected,
      gstPaid,
      gstNet: roundMoney(gstCollected - gstPaid),
    },
    warnings: {
      uncategorisedBusinessCount: uncatCount,
      uncategorisedBusinessGross: roundMoney(uncatGross),
      unassignedCount,
      unassignedGross: roundMoney(unassignedGross),
    },
    businessTxCount,
  };
}

// ---------------------------------------------------------------------------
// Personal (family-books) assessable income hint
// ---------------------------------------------------------------------------

export interface PersonalIncomeHint {
  total: number;
  byCategory: CategoryLine[];
}

/**
 * Assessable income sitting on the FAMILY books in the year (e.g. bank
 * interest on the everyday account). Surfaced as a hint on the personal cards
 * — NOT auto-added, because whether it's joint or one partner's is the user's
 * call. GST-exclusive is irrelevant here (personal interest is input-taxed),
 * so we report the gross magnitude.
 */
export function detectPersonalAssessableIncome(
  transactions: BankTransaction[],
  fy: FinancialYear,
): PersonalIncomeHint {
  const groups = new Map<string, { signed: number; count: number }>();
  for (const t of transactions) {
    if (!inFinancialYear(t.date, fy)) continue;
    if (t.scope !== 'personal') continue;
    if (!t.category.startsWith('income:')) continue;
    const treat = defaultTreatment(t.category);
    if (!treat.assessable) continue;
    const g = groups.get(t.category) || { signed: 0, count: 0 };
    g.signed += t.amount;
    g.count += 1;
    groups.set(t.category, g);
  }
  const byCategory: CategoryLine[] = Array.from(groups.entries())
    .map(([category, g]) => ({
      category,
      label: getCategoryLabel(category),
      gross: roundMoney(Math.abs(g.signed)),
      exGst: roundMoney(Math.abs(g.signed)),
      gst: 0,
      count: g.count,
      gstApplicable: false,
    }))
    .sort((a, b) => b.gross - a.gross);
  return { total: roundMoney(byCategory.reduce((s, l) => s + l.gross, 0)), byCategory };
}

// ---------------------------------------------------------------------------
// Personal returns (distribution → taxable → tax)
// ---------------------------------------------------------------------------

export interface PersonalReturnsOptions {
  fy: FinancialYear;
  netPartnershipIncome: number;
  /** Fraction (0..1) of net income to partner A; B gets the remainder. */
  splitA: number;
  partnerAName: string;
  partnerBName: string;
  otherIncomeA: number;
  otherIncomeB: number;
  deductionsA: number;
  deductionsB: number;
  /** Deductible personal (concessional) super contributions per partner. */
  superA: number;
  superB: number;
  dependents: number;
}

export interface PersonalReturn {
  name: string;
  distribution: number;
  otherIncome: number;
  deductions: number;
  /** Deductible super contribution reducing this partner's taxable income. */
  super: number;
  taxable: number;
  tax: IndividualTaxResult;
}

export function buildPersonalReturns(opts: PersonalReturnsOptions): [PersonalReturn, PersonalReturn] {
  const net = Number(opts.netPartnershipIncome) || 0;
  const splitA = Math.min(1, Math.max(0, Number(opts.splitA) || 0));
  const distA = roundMoney(net * splitA);
  const distB = roundMoney(net - distA);

  const taxableA = Math.max(0, roundMoney(distA + (opts.otherIncomeA || 0) - (opts.deductionsA || 0) - (opts.superA || 0)));
  const taxableB = Math.max(0, roundMoney(distB + (opts.otherIncomeB || 0) - (opts.deductionsB || 0) - (opts.superB || 0)));
  const familyTaxableIncome = roundMoney(taxableA + taxableB);

  const make = (name: string, distribution: number, otherIncome: number, deductions: number, superContrib: number, taxable: number): PersonalReturn => ({
    name,
    distribution,
    otherIncome: roundMoney(otherIncome || 0),
    deductions: roundMoney(deductions || 0),
    super: roundMoney(superContrib || 0),
    taxable,
    tax: estimateIndividualTax({ taxable, fy: opts.fy, familyTaxableIncome, dependents: opts.dependents }),
  });

  return [
    make(opts.partnerAName, distA, opts.otherIncomeA, opts.deductionsA, opts.superA, taxableA),
    make(opts.partnerBName, distB, opts.otherIncomeB, opts.deductionsB, opts.superB, taxableB),
  ];
}
